/**
 * The notes section inside the row drawer.
 *
 * Ian, 2026-08-13: "i might be currently working on something and dont finish
 * it i want to jot down my thoughts for the next day."
 *
 * Notes belong to the CAR, not to the flag it happens to be under today —
 * tomorrow's run may file it differently, or clear it, and that is exactly
 * when yesterday's note is worth reading. Each note records the flag its
 * author was working, so the context is not lost.
 *
 * ADD-ONLY (Ian): no edit, no delete. A note is a record of what someone
 * thought at a moment; a wrong one is corrected by writing another underneath,
 * the way a paper log works.
 *
 * Two plainly-labelled sections — the box to add one, then the notes (Ian:
 * "add note above notes") — and the
 * button is the only way to save. There was a Ctrl+Enter shortcut; Ian, 2026-08-13:
 * "the keyboard controls will be confusing". A hidden way to save is worse than
 * no shortcut, because the visible way then looks optional.
 */
import React, { useEffect, useState } from "react"

import {
  addVehicleNote,
  listVehicleNotes,
  VehicleNoteEntry,
} from "../../../api/audit"
import { THEME } from "../../../config/theme"
import { parseUtcTimestamp } from "../../../utils/parseUtcTimestamp"

interface Props {
  vin: string | null
  dealerGroupId: number
  /** The flag the drawer was opened from — stored as the note's context. */
  problemType: string | null
  /** Registry lookup so a note's context reads as a label, not a slug. */
  labelFor?: (slug: string) => string
}

const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
  marginBottom: 6,
}

const quietText: React.CSSProperties = {
  fontSize: 12.5,
  color: THEME.colors.neutral.light.textSecondary,
}

function stamp(iso: string): string {
  return parseUtcTimestamp(iso).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  })
}

export default function VehicleNotes({ vin, dealerGroupId, problemType, labelFor }: Props) {
  const [notes, setNotes] = useState<VehicleNoteEntry[] | null>(null)
  const [draft, setDraft] = useState("")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!vin) return
    let cancelled = false
    setNotes(null)
    listVehicleNotes(vin, dealerGroupId)
      // Guarded, not trusted: a response without `notes` would otherwise leave
      // state undefined and the render below throws on `.length`, taking the
      // whole drawer down. Second time today this shape of read has bitten —
      // see the error-boundary entry in .claude/cleanup-tasks.md.
      .then((response) => { if (!cancelled) setNotes(response?.notes ?? []) })
      .catch(() => { if (!cancelled) setNotes([]) })
    return () => { cancelled = true }
  }, [vin, dealerGroupId])

  if (!vin) return null

  const handleAdd = async () => {
    const text = draft.trim()
    if (!text || saving) return
    setSaving(true)
    setError(null)
    try {
      const created = await addVehicleNote(vin, dealerGroupId, text, problemType)
      // Prepend rather than refetch: the note is already the newest, and a
      // round trip would blank a list the operator is reading.
      setNotes((current) => (created ? [created, ...(current ?? [])] : (current ?? [])))
      setDraft("")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save that note")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ marginTop: THEME.spacing.md }}>
      <div style={sectionLabel}>Add note</div>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="What did you find? What's left to do?"
        rows={3}
        aria-label="New note"
        style={{
          width: "100%",
          padding: "7px 9px",
          fontSize: 12.5,
          fontFamily: THEME.fonts.primary,
          border: `1px solid ${THEME.colors.silver}`,
          borderRadius: THEME.borderRadius.sm,
          resize: "vertical",
          boxSizing: "border-box",
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: THEME.spacing.sm, marginTop: 6 }}>
        <button
          type="button"
          onClick={handleAdd}
          disabled={draft.trim() === "" || saving}
          style={{
            padding: "6px 14px",
            fontSize: 12.5,
            fontWeight: 500,
            fontFamily: THEME.fonts.primary,
            borderRadius: THEME.borderRadius.sm,
            border: `1px solid ${THEME.colors.silver}`,
            background: THEME.colors.white,
            color: THEME.colors.neutral.light.textPrimary,
            cursor: draft.trim() === "" || saving ? "not-allowed" : "pointer",
            opacity: draft.trim() === "" || saving ? 0.6 : 1,
          }}
        >
          {saving ? "Saving…" : "Add note"}
        </button>
        {error !== null && (
          <span style={{ fontSize: 12, color: THEME.colors.semantic.danger }}>{error}</span>
        )}
      </div>

      <div style={{ ...sectionLabel, marginTop: THEME.spacing.md }}>Notes</div>
      {notes === null ? (
        <div style={quietText}>Loading…</div>
      ) : notes.length === 0 ? (
        <div style={quietText}>No notes on this car yet.</div>
      ) : (
        <div>
          {notes.map((note) => (
            <div
              key={note.id}
              style={{
                borderLeft: `2px solid ${THEME.colors.neutral.light.border}`,
                padding: "4px 0 4px 9px",
                marginBottom: 8,
              }}
            >
              <div style={{ fontSize: 11, color: THEME.colors.neutral.light.textSecondary }}>
                {note.author_name ?? "Unknown"} · {stamp(note.created_at)}
                {note.problem_type && (
                  <> · {labelFor ? labelFor(note.problem_type) : note.problem_type}</>
                )}
              </div>
              <div style={{ fontSize: 12.5, whiteSpace: "pre-wrap", marginTop: 2 }}>
                {note.body}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
